import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  activatePackageType,
  createPackageType,
  deletePackageType,
  listPackageTypes,
  purgePackageType,
  reorderPackageTypes,
  updatePackageType,
  type PackageTypeListParams,
  type PackageTypeListResult,
} from './api';
import type { PackageType, PackageTypeCreateInput } from './types';

const KEY = 'package-types' as const;

export const usePurgePackageType = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => purgePackageType(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const usePackageTypesList = (params: PackageTypeListParams = {}) => {
  return useQuery<PackageTypeListResult, ApiClientError>({
    queryKey: [KEY, 'list', params],
    queryFn: () => listPackageTypes(params),
    placeholderData: (prev) => prev,
  });
};

export const useCreatePackageType = () => {
  const qc = useQueryClient();
  return useMutation<PackageType, ApiClientError, PackageTypeCreateInput>({
    mutationFn: (body) => createPackageType(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useUpdatePackageType = () => {
  const qc = useQueryClient();
  return useMutation<
    PackageType,
    ApiClientError,
    { id: string; body: Partial<PackageTypeCreateInput> }
  >({
    mutationFn: ({ id, body }) => updatePackageType(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useDeletePackageType = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => deletePackageType(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useReorderPackageTypes = () => {
  const qc = useQueryClient();
  return useMutation<PackageType[], ApiClientError, string[]>({
    mutationFn: (orderedIds) => reorderPackageTypes(orderedIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useActivatePackageType = () => {
  const qc = useQueryClient();
  return useMutation<PackageType, ApiClientError, string>({
    mutationFn: (id) => activatePackageType(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};
